import { useCallback, useEffect, useState } from "react";
import { Mail, MailOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiError } from "@/lib/api";

export default function MessagesAdmin() {
  const [messages, setMessages] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (m) => {
    if (m.read) return;
    try {
      await api.put(`/admin/messages/${m.id}/read`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/messages/${deleting.id}`);
      toast.success("Message deleted");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="messages-admin" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy">Contact Messages</h1>
        <p className="mt-1 text-sm text-muted-foreground">Messages sent through the public contact form.</p>
      </div>
      {!messages && <p className="text-sm text-muted-foreground">Loading…</p>}
      {messages && messages.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-white/60 px-8 py-14 text-center" data-testid="messages-empty">
          <Mail className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 font-heading text-lg font-bold text-navy">No messages yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Messages from the contact form will appear here.</p>
        </div>
      )}
      {messages && messages.length > 0 && (
        <div className="space-y-3">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`rounded-lg border bg-white p-5 shadow-sm ${m.read ? "border-border" : "border-gold/50"}`}
              data-testid={`message-${m.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <button
                  className="min-w-0 text-left"
                  onClick={() => { setExpanded(expanded === m.id ? null : m.id); markRead(m); }}
                  data-testid={`message-open-${m.id}`}
                >
                  <p className="flex items-center gap-2 font-semibold text-navy">
                    {m.read ? <MailOpen className="h-4 w-4 text-muted-foreground" /> : <Mail className="h-4 w-4 text-gold-dark" />}
                    {m.name}
                    {!m.read && <span className="rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gold-dark">New</span>}
                  </p>
                  <p className="mt-1 truncate text-xs text-muted-foreground">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString("en-IN")}</span>
                  <Button variant="ghost" size="icon" onClick={() => setDeleting(m)} data-testid={`message-delete-${m.id}`} aria-label="Delete message">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
              <p className={`mt-3 text-sm text-foreground/80 whitespace-pre-line ${expanded === m.id ? "" : "line-clamp-2"}`}>{m.message}</p>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent data-testid="message-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete message?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete the message from {deleting?.name}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="message-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="message-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
